import { useRouter } from "expo-router";

import { ResourceListScreen } from "~/components/composed/ResourceListScreen";
import { Button, ButtonText } from "~/components/ui";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceProjectStatuses,
  useWorkspaceProjects,
} from "~/hooks/workspace";
import {
  PROJECT_BUDGET_OPTIONS,
  projectBudgetTypeLabel,
} from "~/utils/work-planning";

function formatRate(cents: number | null) {
  if (!cents) return "No rate";
  return `$${(cents / 100).toFixed(2)}/h`;
}

function formatBudget(
  type: string,
  cents: number | null,
  hours: number | null,
) {
  if (type === "hours_cap") {
    return hours ? `${hours}h cap` : "No hours cap";
  }
  if (!cents) return `No ${projectBudgetTypeLabel(type).toLowerCase()}`;
  return `${projectBudgetTypeLabel(type)} $${(cents / 100).toFixed(2)}`;
}

function formatWindow(startsOn: string | null, endsOn: string | null) {
  if (startsOn && endsOn) return `${startsOn} to ${endsOn}`;
  if (startsOn) return `Starts ${startsOn}`;
  if (endsOn) return `Ends ${endsOn}`;
  return "No dates";
}

export default function ProjectsScreen() {
  const router = useRouter();
  const workspace = usePrimaryWorkspace();
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const statuses = useWorkspaceProjectStatuses(workspace.data?.id);
  const records = projects.data ?? [];
  const clientRecords = clients.data ?? [];
  const statusRecords = statuses.data ?? [];
  const defaultStatus = statusRecords.find(
    (status) => status.state === "active",
  );
  const statusById = new Map(
    statusRecords.map((status) => [status.id, status]),
  );
  const clientById = new Map(
    clientRecords.map((client) => [client.id, client]),
  );
  const clientOptions = [
    { label: "No client", value: "" },
    ...clientRecords.map((client) => ({
      label: client.name,
      value: client.id,
    })),
  ];
  const statusOptions = [
    { label: "Default status", value: "" },
    ...statusRecords.map((status) => ({
      label: status.name,
      value: status.id,
    })),
  ];

  return (
    <ResourceListScreen
      title="Projects"
      subtitle="Group tasks and tracked time by client, delivery status, fee budget, or hours cap."
      primaryAction="Add project"
      emptyTitle="No projects yet"
      emptyDescription="Create a project to group tasks, time, budgets, and future invoice lines."
      fields={[
        { key: "name", label: "Project name", placeholder: "Website refresh" },
        {
          key: "clientId",
          label: "Client",
          placeholder: "No client",
          options: clientOptions,
          selectTitle: "Choose client",
        },
        {
          key: "statusId",
          label: "Status",
          placeholder: "Default status",
          options: statusOptions,
          selectTitle: "Choose project status",
        },
        {
          key: "hourlyRate",
          label: "Hourly rate",
          placeholder: "120",
          keyboardType: "numeric",
        },
        {
          key: "budgetType",
          label: "Budget type",
          placeholder: "Fixed fee",
          options: PROJECT_BUDGET_OPTIONS,
          selectTitle: "Choose budget type",
        },
        {
          key: "budget",
          label: "Fee budget",
          placeholder: "5000",
          keyboardType: "numeric",
        },
        {
          key: "budgetHours",
          label: "Hours cap",
          placeholder: "40",
          keyboardType: "numeric",
        },
        {
          key: "startsOn",
          label: "Starts on",
          placeholder: "2026-07-01",
        },
        {
          key: "endsOn",
          label: "Ends on",
          placeholder: "2026-08-15",
        },
        {
          key: "description",
          label: "Description",
          placeholder: "Scope, budget notes, or delivery details",
          multiline: true,
        },
      ]}
      requiredField="name"
      readOnly={readOnly}
      metrics={[
        {
          label: "Active projects",
          value: String(records.filter((project) => !project.archived).length),
        },
        {
          label: "Archived",
          value: String(records.filter((project) => project.archived).length),
        },
        {
          label: "Rated projects",
          value: String(
            records.filter((project) => project.hourly_rate_cents).length,
          ),
        },
        {
          label: "Budgeted",
          value: String(
            records.filter(
              (project) => project.budget_cents || project.budget_hours,
            ).length,
          ),
        },
      ]}
      items={records.map((project) => {
        const status = project.status_id
          ? statusById.get(project.status_id)
          : undefined;
        const client = project.client_id
          ? clientById.get(project.client_id)
          : undefined;

        return {
          id: project.id,
          title: project.name,
          description: project.description ?? undefined,
          meta: [
            client?.name ?? "No client",
            status?.name ?? "No status",
            formatRate(project.hourly_rate_cents),
            formatBudget(
              project.budget_type,
              project.budget_cents,
              project.budget_hours,
            ),
            formatWindow(project.starts_on, project.ends_on),
            project.archived ? "Archived" : "Open",
          ],
          editValues: readOnly
            ? undefined
            : {
                budget: project.budget_cents
                  ? String(project.budget_cents / 100)
                  : "",
                budgetHours: project.budget_hours
                  ? String(project.budget_hours)
                  : "",
                budgetType: project.budget_type,
                clientId: project.client_id ?? "",
                description: project.description ?? "",
                endsOn: project.ends_on ?? "",
                hourlyRate: project.hourly_rate_cents
                  ? String(project.hourly_rate_cents / 100)
                  : "",
                name: project.name,
                startsOn: project.starts_on ?? "",
                statusId: project.status_id ?? "",
              },
          actions: (
            <>
              <Button
                compact
                iconName="external-link"
                variant="outline"
                onPress={() => router.push(`/projects/${project.id}` as never)}
              >
                <ButtonText>View details</ButtonText>
              </Button>
              {!readOnly ? (
                <>
                  {statusRecords.map((nextStatus) => (
                    <Button
                      key={nextStatus.id}
                      compact
                      variant={
                        project.status_id === nextStatus.id
                          ? "primary"
                          : "outline"
                      }
                      disabled={projects.updateStatus.isPending}
                      onPress={() =>
                        void projects.updateStatus.mutateAsync({
                          id: project.id,
                          statusId: nextStatus.id,
                        })
                      }
                    >
                      <ButtonText>{nextStatus.name}</ButtonText>
                    </Button>
                  ))}
                  <Button
                    compact
                    variant="outline"
                    disabled={projects.setArchived.isPending}
                    onPress={() =>
                      void projects.setArchived.mutateAsync({
                        id: project.id,
                        archived: !project.archived,
                      })
                    }
                  >
                    <ButtonText>
                      {project.archived ? "Restore" : "Archive"}
                    </ButtonText>
                  </Button>
                </>
              ) : null}
            </>
          ),
        };
      })}
      loading={
        workspace.isLoading ||
        clients.isLoading ||
        projects.isLoading ||
        statuses.isLoading
      }
      error={
        workspace.error ??
        clients.error ??
        projects.error ??
        statuses.error ??
        projects.create.error ??
        projects.update.error ??
        projects.updateStatus.error ??
        projects.setArchived.error
      }
      creating={projects.create.isPending}
      onCreate={(values) =>
        projects.create.mutateAsync({
          ...values,
          statusId: values.statusId || defaultStatus?.id || "",
        })
      }
      updating={projects.update.isPending}
      onUpdate={(id, values) => projects.update.mutateAsync({ id, values })}
    />
  );
}
